import { LiveAnnouncer, LiveAnnouncerMessage } from '@angular/cdk/a11y'
import { inject, Injectable, TemplateRef } from '@angular/core'
import { ActiveToast, IndividualConfig, ToastContainerDirective, ToastrService } from 'ngx-toastr'
import { ToastType } from './toast-type'
import { ToastComponent } from './toast.component'

export interface ToastPayload {
  iconTemplate: TemplateRef<any> | undefined
  messageTemplate: TemplateRef<any> | undefined
  hideClose: boolean | undefined
  type: ToastType
  onClose?: () => void
}

interface ToastOptions extends Partial<IndividualConfig> {
  hideClose: boolean
  iconTemplate: TemplateRef<any>
  hideTitle: boolean
  liveAnnouncementMessage: LiveAnnouncerMessage
  /**
   * When set, the close button calls this function and does not remove the toast.
   * The caller owns the toast and must remove it with {@link ToastService#clear}.
   */
  onClose: () => void
}

const tenSecondsMs = 10_000

const defaultConfig: Partial<IndividualConfig> = {
  positionClass: 'toast-bottom-right',
  tapToDismiss: false,
  timeOut: tenSecondsMs,
  toastComponent: ToastComponent,
  // This must match the total animation time in toast.component.css
  easeTime: 500,
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  toastr = inject(ToastrService)
  liveAnnouncer = inject(LiveAnnouncer)

  set container(toastContainer: ToastContainerDirective) {
    this.toastr.overlayContainer = toastContainer
  }

  error(
    message: string | TemplateRef<any>,
    title = 'Error',
    options?: Partial<ToastOptions>,
  ): ActiveToast<ToastComponent> {
    return this.show(message, title, options, ToastType.Error)
  }

  info(
    message: string | TemplateRef<any>,
    title = 'Info',
    options?: Partial<ToastOptions>,
  ): ActiveToast<ToastComponent> {
    return this.show(message, title, options, ToastType.Info)
  }

  success(
    message: string | TemplateRef<any>,
    title = 'Success',
    options?: Partial<ToastOptions>,
  ): ActiveToast<ToastComponent> {
    return this.show(message, title, options, ToastType.Success)
  }

  warning(
    message: string | TemplateRef<any>,
    title = 'Warning',
    options?: Partial<ToastOptions>,
  ): ActiveToast<ToastComponent> {
    return this.show(message, title, options, ToastType.Warning)
  }

  /**
   * Removes a single toast by id or all shown toasts
   */
  clear(toastId?: number): void {
    this.toastr.clear(toastId)
  }

  private show(
    message: string | TemplateRef<any>,
    title: string,
    options: Partial<ToastOptions> = {},
    type: ToastType,
  ): ActiveToast<ToastComponent> {
    let messageTemplate: TemplateRef<any> | undefined

    if (typeof message === 'object') {
      messageTemplate = message
      message = ''
    }

    const { iconTemplate, hideClose, hideTitle, onClose, ...customConfig } = options
    const payload: ToastPayload = { iconTemplate, messageTemplate, type, hideClose, onClose }
    let override: Partial<IndividualConfig>

    switch (type) {
      case ToastType.Error:
        override = { ...defaultConfig, disableTimeOut: true, ...customConfig, payload }
        break
      default:
        override = { ...defaultConfig, ...customConfig, payload }
    }

    const toast = this.toastr[type](message, hideTitle ? undefined : title, override) as ActiveToast<ToastComponent>

    if (options.liveAnnouncementMessage) {
      this.liveAnnounce(options.liveAnnouncementMessage, toast.toastId)
    }

    return toast
  }

  private liveAnnounce(liveAnnouncementMessage: LiveAnnouncerMessage, toastId: number): void {
    // Appends invisible zero width spaces to force screen readers to detect a content change
    // when the same message is announced multiple times
    const zeroWidth = '\u200B'.repeat(toastId)
    void this.liveAnnouncer.announce(`${liveAnnouncementMessage}${zeroWidth}`)
  }
}
